import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Leaf } from "lucide-react";

const INDUSTRIES = ["Manufacturing","Energy & Utilities","Transportation & Logistics","Technology","Retail","Construction","Agriculture","Finance","Aviation","Other"];
const COUNTRIES = ["United States","United Kingdom","Germany","France","Netherlands","Spain","Italy","Sweden","Norway","Switzerland","Ireland","Canada","Australia","New Zealand","Japan","Singapore","Hong Kong","South Korea","India","United Arab Emirates","Saudi Arabia","Brazil","Mexico","Argentina","South Africa","Kenya","Nigeria","Egypt","Turkey","Poland","Denmark","Finland","Belgium","Austria","Portugal","Chile","Colombia","Indonesia","Thailand","Vietnam","Malaysia","Philippines","Other"];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ company_name:"", email:"", password:"", industry:"", country:"" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form);
      toast.success(`Welcome to CarbonVault, ${u.company_name}`);
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20">
      <div className="flex items-center gap-2 mb-8">
        <div className="w-10 h-10 bg-[#2C5530] rounded-xl flex items-center justify-center"><Leaf className="w-5 h-5 text-white"/></div>
        <span className="text-lg font-medium">Carbon Vaults</span>
      </div>
      <h1 className="text-4xl tracking-tighter font-light">Register your company</h1>
      <p className="text-stone-600 mt-2">Any country · any currency · buy with card or crypto.</p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <Label htmlFor="company">Company name</Label>
          <Input id="company" data-testid="reg-company" required value={form.company_name} onChange={set("company_name")} className="mt-2 h-12 bg-stone-50 border-stone-200"/>
        </div>
        <div>
          <Label>Industry</Label>
          <Select value={form.industry} onValueChange={set("industry")}>
            <SelectTrigger data-testid="reg-industry" className="mt-2 h-12 bg-stone-50 border-stone-200">
              <SelectValue placeholder="Select industry"/>
            </SelectTrigger>
            <SelectContent className="bg-white">
              {INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Country</Label>
          <Select value={form.country} onValueChange={set("country")}>
            <SelectTrigger data-testid="reg-country" className="mt-2 h-12 bg-stone-50 border-stone-200">
              <SelectValue placeholder="Select country"/>
            </SelectTrigger>
            <SelectContent className="bg-white max-h-64">
              {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="email">Work email</Label>
          <Input id="email" data-testid="reg-email" type="email" required value={form.email} onChange={set("email")} className="mt-2 h-12 bg-stone-50 border-stone-200"/>
        </div>
        <div>
          <Label htmlFor="password">Password (min 6)</Label>
          <Input id="password" data-testid="reg-password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="mt-2 h-12 bg-stone-50 border-stone-200"/>
        </div>
        <Button type="submit" disabled={loading} data-testid="reg-submit" className="w-full h-12 bg-[#2C5530] hover:bg-[#1F3D22] text-white rounded-full">
          {loading ? "Creating account…" : "Create account"}
        </Button>
      </form>
      <div className="mt-6 text-sm text-stone-600">
        Already registered? <Link to="/login" className="text-[#2C5530] font-medium">Sign in</Link>
      </div>
    </div>
  );
}
